import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Store, Filter, Wallet } from "lucide-react";
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Empty } from "@/components/ui/empty";
import { SkeletonRows } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, type ListingRow, type WTBRow } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { shortAddr } from "@/lib/auth";
import { StateBadge } from "@/components/StateBadge";

// BGS/CGC abandonnés (aucune donnée existante) ; RAW = carte loose (non gradée).
const GRADERS = ["", "PSA", "Collectaura", "RAW"];
const GRADER_LABELS: Record<string, string> = { RAW: "Loose" };
// PO-29 — note minimale (filtre serveur grade_min) et taille de page.
const GRADE_MINS = ["", "6", "7", "8", "9", "9.5", "10"];
const PAGE_SIZE = 25;

export function Marketplace() {
  const { t, fmtMoney, fmtDate } = useI18n();
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-muted-fg">
            <Store className="h-3.5 w-3.5" />
            Marketplace
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("marketplace.title") || "Graded card marketplace"}
          </h1>
          <p className="mt-1 max-w-xl text-sm text-muted-fg">
            On-chain WTS/WTB orders for graded cards, escrowed via the marketplace contract.
          </p>
        </div>
        <Link to="/sell" className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-fg shadow-soft hover:bg-primary/90">
          List a card
        </Link>
      </header>

      <Tabs defaultValue="wts">
        <TabsList>
          <TabsTrigger value="wts">{t("marketplace.tab.wts") || "Want to sell"}</TabsTrigger>
          <TabsTrigger value="wtb">{t("marketplace.tab.wtb") || "Want to buy"}</TabsTrigger>
        </TabsList>
        <div className="mt-4">
          <TabsContent value="wts">
            <ListingsPanel fmtMoney={fmtMoney} fmtDate={fmtDate} />
          </TabsContent>
          <TabsContent value="wtb">
            <WTBPanel fmtMoney={fmtMoney} fmtDate={fmtDate} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

function GraderFilter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-xs">
      <Filter className="h-3.5 w-3.5 text-muted-fg" />
      <span className="text-muted-fg">Grader</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent text-xs font-medium focus:outline-none"
      >
        {GRADERS.map((g) => (
          <option key={g || "all"} value={g}>
            {g ? (GRADER_LABELS[g] ?? g) : "All"}
          </option>
        ))}
      </select>
    </div>
  );
}

function GradeMinFilter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-xs">
      <span className="text-muted-fg">Grade ≥</span>
      <select
        aria-label="Minimum grade"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent text-xs font-medium focus:outline-none"
      >
        {GRADE_MINS.map((g) => (
          <option key={g || "any"} value={g}>
            {g || "Any"}
          </option>
        ))}
      </select>
    </div>
  );
}

function ListingsPanel({ fmtMoney, fmtDate }: { fmtMoney: (v: number | string | null | undefined, c?: string) => string; fmtDate: (v: string | null | undefined) => string }) {
  const [grader, setGraderRaw] = useState("");
  const [gradeMin, setGradeMinRaw] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState<{ items: ListingRow[]; count: number; total?: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const setGrader = (v: string) => { setGraderRaw(v); setPage(0); };
  const setGradeMin = (v: string) => { setGradeMinRaw(v); setPage(0); };

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const u = new URL("/api/listings", window.location.origin);
      if (grader) u.searchParams.set("grader", grader);
      if (gradeMin) u.searchParams.set("grade_min", gradeMin);
      u.searchParams.set("limit", String(PAGE_SIZE));
      u.searchParams.set("offset", String(page * PAGE_SIZE));
      const r = await api<{ items: ListingRow[]; count: number; total?: number }>(u.toString());
      setData(r);
    } catch (e) {
      setErr((e as Error).message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [grader, gradeMin, page]);

  useEffect(() => {
    load();
  }, [load]);

  const items = data?.items ?? [];
  const total = data?.total ?? data?.count ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const first = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const last = page * PAGE_SIZE + items.length;

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Want To Sell</CardTitle>
          <CardDescription>
            {data ? `${total} active listing${total > 1 ? "s" : ""}` : "—"}
          </CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GraderFilter value={grader} onChange={setGrader} />
          <GradeMinFilter value={gradeMin} onChange={setGradeMin} />
        </div>
      </CardHeader>
      <CardBody>
        <div className="overflow-hidden rounded-md border border-border">
          <Table>
            <THead>
              <TR>
                <TH className="w-10 text-right">#</TH>
                <TH>Card</TH>
                <TH>Name</TH>
                <TH>État</TH>
                <TH>Cert</TH>
                <TH className="text-right">Price (USDC)</TH>
                <TH>Seller</TH>
                <TH>Published</TH>
              </TR>
            </THead>
            <TBody>
              {loading && items.length === 0 ? (
                <SkeletonRows rows={5} cols={8} />
              ) : err ? (
                <tr><td colSpan={8}><Empty title="Failed to load" description={err} /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8}><Empty title="No listings yet" description="Be the first to list a graded card." /></td></tr>
              ) : (
                items.map((l, i) => (
                  <TR key={(l.id ?? i) + ":" + (l.cert_number ?? l.condition ?? "")}>
                    <TD className="text-right text-muted-fg">{page * PAGE_SIZE + i + 1}</TD>
                    <TD>
                      <Link to={`/card/${encodeURIComponent(l.card_id)}`} className="font-mono text-xs text-primary hover:underline">
                        {l.card_id}
                      </Link>
                    </TD>
                    <TD className="max-w-[220px] truncate">{l.card_name ?? "—"}</TD>
                    <TD><StateBadge grader={l.grader} grade={l.grade} condition={l.condition} /></TD>
                    <TD className="font-mono text-xs text-muted-fg">{l.cert_number ?? "—"}</TD>
                    <TD className="text-right font-mono">{fmtMoney(l.price_usdc, "USD")}</TD>
                    <TD className="font-mono text-xs" title={l.seller_wallet}>{shortAddr(l.seller_wallet)}</TD>
                    <TD className="text-xs text-muted-fg">{fmtDate(l.created_at)}</TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-muted-fg">
          <span>{total > 0 ? `${first}–${last} / ${total}` : ""}</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={loading || page === 0}
              className="rounded-md border border-border px-2.5 py-1 font-medium disabled:opacity-40"
            >
              Previous
            </button>
            <span>Page {page + 1} / {pageCount}</span>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={loading || page + 1 >= pageCount}
              className="rounded-md border border-border px-2.5 py-1 font-medium disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

const ONCHAIN_TONE: Record<string, "warn" | "info" | "success" | "danger" | "neutral"> = {
  created: "warn",
  claimed: "info",
  released: "success",
  refunded: "danger",
  "off-chain": "neutral",
};

function WTBPanel({ fmtMoney, fmtDate }: { fmtMoney: (v: number | string | null | undefined, c?: string) => string; fmtDate: (v: string | null | undefined) => string }) {
  const [grader, setGrader] = useState("");
  const [items, setItems] = useState<WTBRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api<{ items: WTBRow[] }>(`/api/wtb`);
      const list = (r?.items || []).filter((w) => !grader || w.grader === grader);
      setItems(list);
    } catch (e) {
      setErr((e as Error).message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [grader]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Want To Buy</CardTitle>
          <CardDescription>{items.length} active offer{items.length > 1 ? "s" : ""}</CardDescription>
        </div>
        <GraderFilter value={grader} onChange={setGrader} />
      </CardHeader>
      <CardBody>
        <div className="overflow-hidden rounded-md border border-border">
          <Table>
            <THead>
              <TR>
                <TH className="w-10 text-right">#</TH>
                <TH>Card</TH>
                <TH>Name</TH>
                <TH>État</TH>
                <TH className="text-right">Price (USDC)</TH>
                <TH>Buyer</TH>
                <TH>On-chain state</TH>
                <TH>Published</TH>
              </TR>
            </THead>
            <TBody>
              {loading && items.length === 0 ? (
                <SkeletonRows rows={4} cols={8} />
              ) : err ? (
                <tr><td colSpan={8}><Empty title="Failed to load" description={err} /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8}><Empty title="No buy orders" icon={<Wallet className="h-5 w-5" />} description="Post a WTB to bid on a graded card." /></td></tr>
              ) : (
                items.map((w, i) => {
                  const st = w.onchain_status || "off-chain";
                  return (
                    <TR key={(w.id ?? i) + ":" + w.buyer_wallet}>
                      <TD className="text-right text-muted-fg">{i + 1}</TD>
                      <TD>
                        <Link to={`/card/${encodeURIComponent(w.card_id)}`} className="font-mono text-xs text-primary hover:underline">
                          {w.card_id}
                        </Link>
                      </TD>
                      <TD className="max-w-[220px] truncate">{w.card_name ?? "—"}</TD>
                      <TD><StateBadge grader={w.grader} grade={w.grade} condition={w.condition} /></TD>
                      <TD className="text-right font-mono">{fmtMoney(w.price_usdc, "USD")}</TD>
                      <TD className="font-mono text-xs" title={w.buyer_wallet}>{shortAddr(w.buyer_wallet)}</TD>
                      <TD>
                        <Badge tone={ONCHAIN_TONE[st] || "neutral"} variant="soft">
                          {st.replace("-", " ")}
                        </Badge>
                      </TD>
                      <TD className="text-xs text-muted-fg">{fmtDate(w.created_at)}</TD>
                    </TR>
                  );
                })
              )}
            </TBody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
}
